// ----- Time formatting -----

export function fmtTime(hhmm) {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    if (Number.isNaN(h)) return hhmm;
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export function fmtHM(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function toLocalTimeInput(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = n => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDatetimeLocal(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ----- Date helpers -----

export function fmtYMD(d) {
    const pad = n => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function parseYMD(s) {
    if (!s) return null;
    const parts = s.slice(0, 10).split("-").map(Number);
    if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function daysUntil(ymd) {
    const d = parseYMD(ymd);
    if (!d) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - today.getTime()) / 86400000);
}

export function formatHorizon(ymd) {
    const d = parseYMD(ymd);
    if (!d) return "";
    return `by ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

export function formatLongDate(ymd) {
    const d = parseYMD(ymd);
    if (!d) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function fmtHistoryDate(ymd) {
    // "2026-02-03" -> "Mon, Feb 3"
    const d = parseYMD(ymd);
    if (!d) return ymd || "";
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export function todayKey() {
    return fmtYMD(new Date());
}

// ----- ISO-week key (used by Weekly Reflection to dedupe per-goal-per-week) -----

export function isoWeekKey(date = new Date()) {
    // Copy so we don't mutate caller's date
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    // Thursday of current week decides the year
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
}

// ----- Christian season helpers (for target-date quick chips) -----

export function easterDate(year) {
    // Anonymous Gregorian algorithm (Western Easter)
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const L = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * L) / 451);
    const month = Math.floor((h + L - 7 * m + 114) / 31);
    const day = ((h + L - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

export function adventStart(year) {
    // First Sunday of Advent = 4th Sunday before Dec 25
    const dec25 = new Date(year, 11, 25);
    const dow = dec25.getDay(); // 0=Sun
    const sundayBeforeXmas = new Date(year, 11, 25 - dow);
    if (dow === 0) sundayBeforeXmas.setDate(sundayBeforeXmas.getDate() - 7);
    sundayBeforeXmas.setDate(sundayBeforeXmas.getDate() - 21);
    return sundayBeforeXmas;
}

export function DATE_QUICK_CHIPS() {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const chips = [];
    const in30 = new Date(today); in30.setDate(today.getDate() + 30);
    chips.push({ key: "in-30-days", label: "In 30 days", value: fmtYMD(in30) });
    const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    if (eom > today) chips.push({ key: "end-of-month", label: "End of month", value: fmtYMD(eom) });
    let easter = easterDate(today.getFullYear());
    if (easter < today) easter = easterDate(today.getFullYear() + 1);
    const daysToEaster = Math.round((easter - today) / 86400000);
    if (daysToEaster <= 200) chips.push({ key: "easter", label: `Easter (${easter.toLocaleDateString(undefined, { month: "short", day: "numeric" })})`, value: fmtYMD(easter) });
    let advent = adventStart(today.getFullYear());
    if (advent < today) advent = adventStart(today.getFullYear() + 1);
    const daysToAdvent = Math.round((advent - today) / 86400000);
    if (daysToAdvent <= 400) chips.push({ key: "advent", label: `Advent (${advent.toLocaleDateString(undefined, { month: "short", day: "numeric" })})`, value: fmtYMD(advent) });
    return chips;
}

// ----- Rhythm helpers -----

// Which slot-times are scheduled to occur today for this rhythm_config?
export function scheduledSlotsForToday(cfg) {
    if (!cfg || !cfg.times || !cfg.times.length) return [];
    const today = new Date();
    const dow = today.getDay();
    const freq = cfg.frequency;
    if (freq === "once") {
        const d = parseYMD(cfg.occasion_date);
        if (!d || d.toDateString() !== today.toDateString()) return [];
        return cfg.times;
    }
    if (freq === "daily") return cfg.times;
    if (freq === "weekly") {
        return (cfg.days_of_week || []).includes(dow) ? cfg.times : [];
    }
    if (freq === "monthly") {
        const dom = cfg.day_of_month;
        if (typeof dom === "number" || /^\d+$/.test(String(dom || ""))) {
            const n = Number(dom);
            const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            const target = Math.min(n, eom);
            return today.getDate() === target ? cfg.times : [];
        }
        if (dom === "last_day") {
            const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            return today.getDate() === eom ? cfg.times : [];
        }
        const m = String(dom || "").match(/^(first|second|third|fourth|last)_(SUN|MON|TUE|WED|THU|FRI|SAT)$/i);
        if (m) {
            const ordinal = m[1].toLowerCase();
            const dayCode = ["SUN","MON","TUE","WED","THU","FRI","SAT"].indexOf(m[2].toUpperCase());
            if (today.getDay() !== dayCode) return [];
            const day = today.getDate();
            const nth = Math.floor((day - 1) / 7) + 1;
            const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
            const isLast = day + 7 > eom;
            const ord = { first: 1, second: 2, third: 3, fourth: 4 }[ordinal];
            if (ordinal === "last") return isLast ? cfg.times : [];
            return ord === nth ? cfg.times : [];
        }
        return [];
    }
    return [];
}

// Build a legacy rhythm_config for goals created before the rhythm rebuild.
export function legacyRhythmConfig(g) {
    if (g.rhythm_config) return g.rhythm_config;
    if (g.rhythm === "times_of_day") {
        const map = { morning: "08:00", midday: "12:00", evening: "20:00" };
        return {
            frequency: "daily",
            times: (g.time_slots || []).map(s => ({ label: s, time: map[s] })),
            reminders_enabled: true,
        };
    }
    if (g.rhythm === "daily") return { frequency: "daily", times: [], reminders_enabled: true };
    if (g.rhythm === "weekly") return { frequency: "weekly", days_of_week: [0], times: [], reminders_enabled: true };
    return { frequency: "none", times: [], reminders_enabled: false };
}
